import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { MessageCircle, X, Send, Paperclip, RotateCcw, Loader2, Sparkles, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, errMsg } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { VoiceButton } from "../VoiceRecorder";

const newSession = () => {
  const id = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  localStorage.setItem("sandy_session", id);
  return id;
};

const SALES_TEASERS = {
  "/for-venues": { text: "Ask Sandy how Gulf Coast VIP can help your venue.", prompt: "How can Gulf Coast VIP help my venue?" },
  "/for-artists": { text: "Ask Sandy which artist plan fits your goals.", prompt: "Which artist plan fits my goals?" },
  "/for-attractions": { text: "Ask Sandy how to showcase rides, games, tickets and deals.", prompt: "How do I showcase rides, games, tickets and deals?" },
  "/for-activities": { text: "Ask Sandy how schedules and bookings work.", prompt: "How do schedules and bookings work for activities?" },
  "/for-users": { text: "Ask Sandy what's happening near you.", prompt: "What's happening near me this weekend?" },
};

function MdLite({ text }) {
  const lines = (text || "").split("\n");
  return (
    <div className="space-y-1">
      {lines.map((ln, i) => {
        const parts = [];
        let rest = ln, key = 0;
        const rx = /\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)]+)\)/;
        while (rest) {
          const m = rest.match(rx);
          if (!m) { parts.push(rest); break; }
          if (m.index > 0) parts.push(rest.slice(0, m.index));
          if (m[1] !== undefined) parts.push(<strong key={key++}>{m[1]}</strong>);
          else parts.push(<Link key={key++} to={m[3]} className="text-rose-400 underline">{m[2]}</Link>);
          rest = rest.slice(m.index + m[0].length);
        }
        const isBullet = /^\s*[-•]\s/.test(ln);
        return <p key={i} className={isBullet ? "pl-3" : ""}>{parts}</p>;
      })}
    </div>
  );
}

const FIELD_LABELS = {
  title: "Title", description: "Description", event_date: "Date", end_date: "End date", start_time: "Start",
  end_time: "End", venue_name: "Venue", venue_id: null, artist_ids: null, artist_names: "Artists",
  event_image: null, image: null, event_type: "Type", genre: "Genre", ticket_price: "Ticket price",
  ticket_link: "Ticket link", age_restriction: "Age restriction", recurrence_type: "Repeats",
  recurrence_until: "Until", recurrence_weekdays: null, city: "City", state: "State",
  budget_min: "Budget min", budget_max: "Budget max", payment_type: "Payment", location_name: "Location",
  application_deadline: "Deadline", job_category: "Category", job_type: "Job type", pay_min: "Pay min",
  pay_max: "Pay max", pay_type: "Pay type", contact_email: "Contact", business_name: "Business",
  short_description: "Summary", original_price: "Original price", discounted_price: "Deal price",
  start_datetime: "Starts", end_datetime: "Ends", quantity_available: "Quantity",
  redemption_instructions: "Redemption", name: "Name", email: "Email", issue_type: "Issue",
  summary: "Summary", priority: "Priority", seo_title: null, seo_description: null,
};

const ACTION_TITLES = {
  create_event: "Create this event?", create_gig: "Post this gig?", create_hiring: "Post this job?",
  create_deal: "Create this deal?", support_ticket: "Send this support ticket?",
};

function ConfirmCard({ action, sessionId, onDone }) {
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState(null); // {ok, link, status} | {error}
  const [dates, setDates] = useState(null);
  const d = action.data || {};

  useEffect(() => {
    if (d.recurrence_type && d.recurrence_type !== "single" && d.event_date) {
      api.post("/events/preview-recurrence", { event_date: d.event_date, recurrence_type: d.recurrence_type, recurrence_until: d.recurrence_until, recurrence_weekdays: d.recurrence_weekdays })
        .then((r) => setDates(r.data.dates)).catch(() => {});
    }
  }, []); // eslint-disable-line

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/assistant/execute", { session_id: sessionId, action });
      setState({ ok: true, link: data.link, status: data.record_status });
      onDone && onDone(true);
    } catch (e) {
      setState({ error: errMsg(e) });
      onDone && onDone(false);
    }
    setBusy(false);
  };

  if (state?.ok) return (
    <div className="mt-2 rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm" data-testid="sandy-action-success">
      <p className="font-semibold text-emerald-400 flex items-center gap-1.5"><Check size={15} /> Done! Status: {state.status || "submitted"}</p>
      {state.link && <Link to={state.link} className="text-rose-400 underline text-xs flex items-center gap-1 mt-1">View it here <ExternalLink size={11} /></Link>}
    </div>
  );

  return (
    <div className="mt-2 rounded-xl border border-rose-500/40 bg-[#141414] p-3 text-sm" data-testid="sandy-confirm-card">
      <p className="font-bold text-rose-400 mb-1.5">{ACTION_TITLES[action.type] || "Confirm this action?"}</p>
      {action.summary && <p className="text-zinc-300 mb-2 text-xs">{action.summary}</p>}
      <div className="space-y-0.5 mb-2">
        {Object.entries(d).map(([k, v]) => {
          const label = FIELD_LABELS[k];
          if (label === null || v === "" || v == null || (Array.isArray(v) && !v.length)) return null;
          return <div key={k} className="flex gap-2 text-xs"><span className="text-zinc-500 w-24 shrink-0">{label || k.replace(/_/g, " ")}</span><span className="text-zinc-200 break-words min-w-0">{Array.isArray(v) ? v.join(", ") : String(v)}</span></div>;
        })}
        {(d.event_image || d.image) && <img src={fileUrl(d.event_image || d.image)} alt="" className="mt-1 rounded-lg max-h-28 object-cover" />}
      </div>
      {dates && (
        <div className="mb-2 text-xs bg-white/5 rounded-lg p-2" data-testid="sandy-recurrence-preview">
          <p className="text-zinc-400 mb-1 font-semibold">This will create {dates.length} events:</p>
          <p className="text-zinc-300 break-words">{dates.slice(0, 12).join(", ")}{dates.length > 12 ? ` …and ${dates.length - 12} more` : ""}</p>
        </div>
      )}
      {state?.error && <p className="text-xs text-red-400 mb-2" data-testid="sandy-action-error">{state.error}</p>}
      <div className="flex gap-2">
        <button onClick={confirm} disabled={busy} data-testid="sandy-confirm-btn"
          className="flex-1 bg-rose-500 hover:bg-rose-600 disabled:opacity-50 rounded-lg py-1.5 text-xs font-bold flex items-center justify-center gap-1">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Confirm & Submit
        </button>
        <button onClick={() => onDone && onDone(false, true)} disabled={busy} data-testid="sandy-cancel-btn"
          className="px-3 rounded-lg border border-white/15 text-xs text-zinc-400 hover:text-white">Cancel</button>
      </div>
    </div>
  );
}

export default function SandyWidget() {
  const { user } = useAuth();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("sandy_session") || newSession());
  const [teaser, setTeaser] = useState(null);
  const [hidden, setHidden] = useState(() => localStorage.getItem("sandy_hidden") === "1");
  const bottomRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => api.get("/assistant/config").then((r) => setCfg(r.data)).catch(() => setCfg({ enabled: false })), 800);
    return () => clearTimeout(t);
  }, [user]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);
  useEffect(() => {
    const openHandler = (e) => {
      localStorage.removeItem("sandy_hidden");
      setHidden(false);
      setOpen(true);
      const prompt = e?.detail?.prompt;
      if (prompt) setInput(prompt);
    };
    window.addEventListener("sandy:open", openHandler);
    return () => window.removeEventListener("sandy:open", openHandler);
  }, []);
  useEffect(() => {
    // Respect the persistent dismiss across sessions — once the user closes
    // the teaser, don't pop it again on the next visit or on sales pages.
    if (localStorage.getItem("sandy_teaser_dismissed") === "1") return;
    const st = SALES_TEASERS[loc.pathname];
    if (st) { setTeaser(st); return; }
    const t = setTimeout(() => api.get("/assistant/teaser").then((r) => r.data.text && setTeaser(r.data)).catch(() => {}), 1500);
    return () => clearTimeout(t);
  }, [loc.pathname]);

  const dismissTeaser = () => { localStorage.setItem("sandy_teaser_dismissed", "1"); setTeaser(null); };

  const send = useCallback(async (text) => {
    const message = (text || "").trim();
    if (!message || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", content: message }]);
    setBusy(true);
    try {
      const { data } = await api.post("/assistant/chat", { session_id: sessionId, message, page_context: window.location.pathname });
      setMsgs((m) => [...m, { role: "assistant", content: data.reply, links: data.links, action: data.action, suggestions: data.suggestions }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", content: errMsg(e), error: true }]);
    }
    setBusy(false);
  }, [busy, sessionId]);

  const uploadFlyer = async (file) => {
    if (!file) return;
    if (!user) { toast.error("Please log in to upload a flyer"); return; }
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: `📎 Uploaded flyer: ${file.name}` }]);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/assistant/upload-flyer", fd);
      const ex = data.extracted || {};
      const filled = Object.entries(ex).filter(([, v]) => v && (!Array.isArray(v) || v.length)).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`).join("\n");
      setBusy(false);
      await send(`I uploaded an event flyer (image path: ${data.image_path}). Here is what was read from it — please verify with me before using anything uncertain:\n${filled || "Nothing could be read from the flyer."}`);
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", content: errMsg(e), error: true }]);
      setBusy(false);
    }
  };

  const restart = () => { setSessionId(newSession()); setMsgs([]); };

  const [transcribing, setTranscribing] = useState(false);
  const onVoice = async (file) => {
    setTranscribing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/assistant/voice", fd);
      setTranscribing(false);
      await send(data.text);
    } catch (e) {
      setTranscribing(false);
      toast.error(errMsg(e));
    }
  };

  if (loc.pathname.startsWith("/admin/builder")) return null;
  if (!cfg || !cfg.enabled) return null;

  return (
    <>
      {!open && hidden && (
        <button onClick={() => { localStorage.removeItem("sandy_hidden"); setHidden(false); }} data-testid="sandy-mini-btn" title={`Reopen ${cfg.name}`}
          className="fixed bottom-24 sm:bottom-5 right-5 z-50 w-10 h-10 rounded-full bg-rose-500/60 hover:bg-rose-500 text-white flex items-center justify-center shadow-lg transition-colors">
          <Sparkles size={16} />
        </button>
      )}
      {!open && !hidden && (
        <div className="fixed bottom-24 sm:bottom-5 right-5 z-50 flex flex-col items-end gap-2">
          {teaser && (
            <div className="relative max-w-[260px] bg-[#141414] border border-rose-500/40 rounded-2xl rounded-br-sm px-4 py-3 shadow-xl animate-in fade-in slide-in-from-bottom-2 duration-500" data-testid="sandy-teaser">
              <button onClick={dismissTeaser} data-testid="sandy-teaser-dismiss"
                className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-zinc-700 hover:bg-zinc-600 flex items-center justify-center"><X size={11} /></button>
              <button onClick={() => { dismissTeaser(); setOpen(true); if (teaser.prompt) send(teaser.prompt); }}
                className="text-left text-sm text-zinc-200 hover:text-white" data-testid="sandy-teaser-cta">{teaser.text}</button>
            </div>
          )}
          <div className="relative">
            <button onClick={() => { setTeaser(null); localStorage.setItem("sandy_hidden", "1"); setHidden(true); }} data-testid="sandy-hide-btn" title="Hide"
              className="absolute -top-2 -right-2 z-10 w-5 h-5 rounded-full bg-zinc-800 border border-white/20 hover:bg-zinc-700 text-zinc-300 flex items-center justify-center"><X size={11} /></button>
            <button onClick={() => { dismissTeaser(); setOpen(true); }} data-testid="sandy-open-btn"
              className="flex items-center gap-2 bg-rose-500 hover:bg-rose-600 text-white rounded-full pl-3 pr-4 py-3 shadow-xl shadow-rose-500/25 transition-transform hover:scale-105">
              <Sparkles size={18} /><span className="text-sm font-bold hidden sm:inline">Ask {cfg.name}</span>
              <span className="text-sm font-bold sm:hidden">{cfg.name}</span>
            </button>
          </div>
        </div>
      )}
      {open && (
        <div className="fixed inset-0 sm:inset-auto sm:bottom-5 sm:right-5 z-50 sm:w-[400px] sm:h-[620px] sm:max-h-[calc(100vh-40px)] bg-[#0d0d0d] sm:rounded-2xl border border-white/10 shadow-2xl flex flex-col overflow-hidden" data-testid="sandy-panel">
          <div className="flex items-center gap-2.5 px-4 py-3 border-b border-white/10 bg-[#111]">
            <div className="w-8 h-8 rounded-full bg-rose-500 flex items-center justify-center"><Sparkles size={16} /></div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm leading-tight">{cfg.name}</p>
              <p className="text-[11px] text-zinc-500">Gulf Coast VIP Assistant</p>
            </div>
            <button onClick={restart} title="Restart conversation" data-testid="sandy-restart-btn" className="p-1.5 text-zinc-500 hover:text-white"><RotateCcw size={16} /></button>
            <button onClick={() => setOpen(false)} data-testid="sandy-close-btn" className="p-1.5 text-zinc-500 hover:text-white"><X size={18} /></button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 text-sm" data-testid="sandy-messages">
            {msgs.length === 0 && (
              <div className="pt-2">
                <p className="text-zinc-300 mb-1">Hey{user ? ` ${user.name.split(" ")[0]}` : ""}! I'm <strong>{cfg.name}</strong>, your Gulf Coast VIP concierge. 🌴</p>
                <p className="text-zinc-500 text-xs mb-3">I can find events, artists, venues, deals and gigs — or help you create listings right here in chat.</p>
                <div className="flex flex-wrap gap-1.5">
                  {(cfg.starters || []).map((s) => (
                    <button key={s} onClick={() => send(s)} data-testid="sandy-starter-chip"
                      className="text-xs border border-white/15 rounded-full px-3 py-1.5 text-zinc-300 hover:border-rose-500 hover:text-white">{s}</button>
                  ))}
                </div>
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : ""}>
                <div className={m.role === "user"
                  ? "bg-rose-500/90 text-white rounded-2xl rounded-br-sm px-3.5 py-2 max-w-[85%]"
                  : `bg-white/5 ${m.error ? "border border-red-500/40" : ""} rounded-2xl rounded-bl-sm px-3.5 py-2 max-w-[92%] text-zinc-200`}>
                  <MdLite text={m.content} />
                  {m.links?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {m.links.map((l, j) => (
                        <Link key={j} to={l.href} className="inline-flex items-center gap-1 text-[11px] bg-rose-500/15 text-rose-300 border border-rose-500/30 rounded-full px-2.5 py-1 hover:bg-rose-500/25" data-testid="sandy-link-chip">
                          {l.label} <ExternalLink size={10} />
                        </Link>
                      ))}
                    </div>
                  )}
                  {m.action && <ConfirmCard action={m.action} sessionId={sessionId}
                    onDone={(ok, cancelled) => cancelled && setMsgs((mm) => mm.map((x, xi) => xi === i ? { ...x, action: null } : x))} />}
                  {m.suggestions?.length > 0 && i === msgs.length - 1 && !busy && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {m.suggestions.map((s, j) => (
                        <button key={j} onClick={() => send(s)} className="text-[11px] border border-white/15 rounded-full px-2.5 py-1 text-zinc-400 hover:text-white hover:border-rose-500" data-testid="sandy-suggestion-chip">{s}</button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {busy && <div className="flex items-center gap-2 text-zinc-500 text-xs"><Loader2 size={14} className="animate-spin" /> {cfg.name} is thinking…</div>}
            <div ref={bottomRef} />
          </div>

          <div className="p-3 border-t border-white/10 bg-[#111]">
            <div className="flex items-end gap-2">
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                onChange={(e) => { uploadFlyer(e.target.files?.[0]); e.target.value = ""; }} />
              <button onClick={() => fileRef.current?.click()} title="Upload event flyer" data-testid="sandy-upload-btn"
                className="p-2.5 rounded-xl border border-white/15 text-zinc-400 hover:text-white hover:border-rose-500"><Paperclip size={16} /></button>
              <VoiceButton onAudio={onVoice} busy={transcribing} disabled={busy} maxSeconds={120} testId="sandy-voice-btn" />
              <textarea value={input} onChange={(e) => setInput(e.target.value)} rows={1} data-testid="sandy-input"
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); } }}
                placeholder={`Ask ${cfg.name} anything…`}
                className="flex-1 bg-[#0a0a0a] border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-rose-500 resize-none max-h-28" />
              <button onClick={() => send(input)} disabled={busy || !input.trim()} data-testid="sandy-send-btn"
                className="p-2.5 rounded-xl bg-rose-500 hover:bg-rose-600 disabled:opacity-40 text-white"><Send size={16} /></button>
            </div>
            <p className="text-[10px] text-zinc-600 mt-1.5 text-center">Powered by Gulf Coast VIP</p>
          </div>
        </div>
      )}
    </>
  );
}
